<template>
  <div class="chart-container">
    <div class="chart-wrapper">
      <canvas ref="chartCanvas"></canvas>
    </div>
  </div>
</template>

<script>
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  LineController
} from 'chart.js';
import { ref, onMounted, onUnmounted } from 'vue';

// Register Chart.js components including the controller
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  LineController
);

export default {
  name: 'PercentageChart',
  props: {
    finalPercentage: {
      type: Number,
      required: true
    },
    predictionId: {
      type: Number,
      required: true
    }
  },
  setup(props) {
    const chartCanvas = ref(null);
    let chartInstance = null;
    
    // Function to generate sample data points that lead to the final percentage
    const generateChartData = (finalValue, predictionId) => {
      // Generate 10-15 data points with some randomness but leading to final value
      const dataPoints = 10; // 10-15 points based on predictionId
      const data = [];
      const initialPercentage = Math.random() * 30 + 5; // Start between 5-35%
      
      for (let i = 0; i < dataPoints; i++) {
        // Calculate how far along we are in the sequence
        const progress = i / (dataPoints - 1);
        
        // Calculate value with some randomness that trends toward final value
        let value;
        if (i === dataPoints - 1) {
          // Last value is exactly the final percentage
          value = finalValue;
        } else {
          // Calculate a value that trends toward the final value with some variation
          const trendValue = initialPercentage + (finalValue - initialPercentage) * progress;
          // Add random variation within 5% of the trend value
          const variation = (Math.random() - 0.5) * 10; // -5% to +5%
          value = Math.max(0, Math.min(100, trendValue + variation));
        }
        
        data.push(Math.round(value * 100) / 100); // Round to 2 decimal places
      }
      
      // Generate labels (e.g., time periods)
     const labels = [];
     const today = new Date();
     for (let i = 0; i < dataPoints; i++) {
       const date = new Date(today);
       date.setDate(today.getDate() - (dataPoints - 1 - i));
       const day = String(date.getDate()).padStart(2, '0');
       const month = String(date.getMonth() + 1).padStart(2, '0');
       labels.push(`${day}.${month}`); // "DD.MM" format
     }
      
      return {
        labels,
        datasets: [
          {
            label: 'Процент',
            data: data,
            borderColor: '#5b6ef5',
            backgroundColor: 'rgba(91, 110, 245, 0.1)',
            tension: 0.4,
            // fill: true,
          }
        ]
      };
    };
    
    onMounted(() => {
      if (chartCanvas.value) {
        const chartData = generateChartData(props.finalPercentage, props.predictionId);
        
        chartInstance = new ChartJS(chartCanvas.value, {
          type: 'line',
          data: chartData,
          options: {
            responsive: true,
            maintainAspectRatio: true,
            interaction: {
              intersect: false,
              mode: 'index' // Enable hover interaction for all points in the dataset
            },
            plugins: {

              legend: {
                display: false
              },
              tooltip: {
                enabled: true,
                mode: 'index',
                intersect: false,
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                titleColor: '#ffffff',
                bodyColor: '#ffffff',
                borderColor: '#5b6ef5',
                borderWidth: 1,
                padding: 10,
                callbacks: {
                  // Customize the tooltip label
                  label: function(context) {
                    return `${context.dataset.label}: ${context.parsed.y}%`;
                  },
                  // Customize the tooltip title
                  title: function(tooltipItems) {
                    return `Период: ${tooltipItems[0].label}`;
                  }
                }
              }
            },
            scales: {
              y: {
               // min: 0,
               // max: 100,
                title: {
                  display: false,
                  text: 'Процент'
                },
                ticks: {
                  callback: function(value) {
                    return value + '%';
                  }
                },
                grid: {
                  display: true
                }
              },
              x: {
                title: {
                  display: false,
                  text: 'Период'
                },
                grid: {
                  display: false
                }
              }
            }
          }
        });
      }
    });
    
    onUnmounted(() => {
      if (chartInstance) {
        chartInstance.destroy();
      }
    });
    
    return {
      chartCanvas
    };
  }
};
</script>

<style scoped>
.chart-container {
  padding-top: 1.5rem;
  background-color: #ffffff;
  /* border-radius: 12px; */
  /* box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05); */
}

.chart-wrapper {
  position: relative;
  width: 100%;
}

h3 {
  margin-top: 0;
  margin-bottom: 1.5rem;
  color: #1f2937;
  font-size: 1.25rem;
}
</style>